/**
 * InteractionManager - Manages player interactions with objects in the world
 * Handles detection of nearby interactable objects and triggering interactions
 */

import type { Player } from '../game/entities/Player';
import type { Door } from '../game/entities/Door';
import type { Vent } from '../game/entities/Vent';
import type { HidingSpot } from '../game/entities/HidingSpot';
import type { InteractiveObject } from '../game/entities/InteractiveObject';
import type { SpawnPoint } from '../game/entities/SpawnPoint';
import { CheckpointSystem } from '../game/systems/CheckpointSystem';
import { getInputManager } from './InputManager';

export type InteractableEntity = Door | Vent | HidingSpot | InteractiveObject | SpawnPoint;

export interface InteractionPrompt {
  message: string;
  key: string;
  target: InteractableEntity;
}

export class InteractionManager {
  private player: Player | null = null;
  private interactables: Map<string, InteractableEntity> = new Map();
  private nearbyInteractables: InteractableEntity[] = [];
  private currentPrompt: InteractionPrompt | null = null;
  private interactionRadius: number = 100; // pixels - increased for better checkpoint detection

  // Door teleportation tracking
  private lastDoorEntryPosition: { x: number; y: number } | null = null;
  private lastDoorId: string | null = null;

  // Callbacks
  private onPromptChange: ((prompt: InteractionPrompt | null) => void) | null = null;
  private onInteractionComplete: ((target: InteractableEntity) => void) | null = null;
  private onFindSpawnPoint: ((spawnId: string) => { x: number; y: number } | null) | null = null;

  constructor() {
    console.log('InteractionManager: Initialized');
  }

  /**
   * Set the player entity
   */
  public setPlayer(player: Player): void {
    this.player = player;
  }

  /**
   * Register an interactable entity
   */
  public registerInteractable(entity: InteractableEntity): void {
    if (!entity.id) {
      console.warn('InteractionManager: Cannot register entity without ID');
      return;
    }

    this.interactables.set(entity.id, entity);
    console.log(`InteractionManager: Registered ${entity.type} - ${entity.id}`);
  }

  /**
   * Unregister an interactable entity
   */
  public unregisterInteractable(entityId: string): void {
    if (this.interactables.has(entityId)) {
      this.interactables.delete(entityId);
      console.log(`InteractionManager: Unregistered ${entityId}`);
    }
  }

  /**
   * Update interaction system - call once per frame
   */
  public update(_deltaTime: number): void {
    if (!this.player) {
      return;
    }

    // Find nearby interactables
    this.updateNearbyInteractables();

    // Update interaction prompt
    this.updatePrompt();

    // NOTE: Interaction input (E key) is handled entirely by TestScene's keydown handler,
    // which applies the green-prompt gate. Do NOT call handleInteractionInput() here.
  }

  /**
   * Find all interactables within range of the player
   */
  private updateNearbyInteractables(): void {
    if (!this.player) {
      this.nearbyInteractables = [];
      return;
    }

    const previousCount = this.nearbyInteractables.length;
    this.nearbyInteractables = [];

    for (const entity of this.interactables.values()) {
      if (!entity.active || entity.destroyed) {
        continue;
      }

      let distance = this.calculateDistance(
        this.player.position,
        entity.position
      );

      // For vents, also consider distance to the exit end so the player
      // can interact from either side of the vent tunnel.
      if (entity.type === 'vent') {
        const exitPos = (entity as any).getExitPosition?.();
        if (exitPos) {
          const exitDist = this.calculateDistance(this.player.position, exitPos);
          distance = Math.min(distance, exitDist);
        }
      }

      const entityRadius = ('interactionRadius' in entity) ? (entity as any).interactionRadius as number : this.interactionRadius;
      const effectiveRadius = Math.max(this.interactionRadius, entityRadius);
      if (distance <= effectiveRadius) {
        this.nearbyInteractables.push(entity);

        // Update entity's nearby state if it has the method
        if ('setPlayerNearby' in entity && typeof entity.setPlayerNearby === 'function') {
          entity.setPlayerNearby(true);
        }

        // For vents, independently highlight whichever end the player is near
        if (entity.type === 'vent') {
          const entranceDist = this.calculateDistance(this.player.position, entity.position);
          const exitPos = (entity as any).getExitPosition?.();
          const exitDist = exitPos ? this.calculateDistance(this.player.position, exitPos) : Infinity;
          if ('setPlayerNearEntrance' in entity) (entity as any).setPlayerNearEntrance(entranceDist <= effectiveRadius);
          if ('setPlayerNearExit' in entity) (entity as any).setPlayerNearExit(exitDist <= effectiveRadius);
        }
      } else {
        // Player moved away
        if ('setPlayerNearby' in entity && typeof entity.setPlayerNearby === 'function') {
          entity.setPlayerNearby(false);
        }
        if (entity.type === 'vent') {
          if ('setPlayerNearEntrance' in entity) (entity as any).setPlayerNearEntrance(false);
          if ('setPlayerNearExit' in entity) (entity as any).setPlayerNearExit(false);
        }
      }
    }

    // Sort by distance (closest first)
    this.nearbyInteractables.sort((a, b) => {
      const distA = this.calculateDistance(this.player!.position, a.position);
      const distB = this.calculateDistance(this.player!.position, b.position);
      return distA - distB;
    });

    // Debug log when nearby interactables change
    if (this.nearbyInteractables.length !== previousCount && this.nearbyInteractables.length > 0) {
      console.log(`🎯 Found ${this.nearbyInteractables.length} nearby interactables:`,
        this.nearbyInteractables.map(e => `${e.type} (${e.id})`));
    }
  }

  /**
   * Update the current interaction prompt
   */
  private updatePrompt(): void {
    if (this.nearbyInteractables.length === 0) {
      if (this.currentPrompt !== null) {
        this.currentPrompt = null;
        if (this.onPromptChange) {
          this.onPromptChange(null);
        }
      }
      return;
    }

    // Get the closest interactable
    const closest = this.nearbyInteractables[0];
    if (!closest) {
      return;
    }

    const message = this.getInteractionMessage(closest);

    const newPrompt: InteractionPrompt = {
      message,
      key: 'E',
      target: closest,
    };

    // Only update if the prompt changed
    if (!this.currentPrompt || this.currentPrompt.target.id !== newPrompt.target.id) {
      this.currentPrompt = newPrompt;
      if (this.onPromptChange) {
        this.onPromptChange(newPrompt);
      }
    }
  }

  /**
   * Get the interaction message for an entity
   */
  private getInteractionMessage(entity: InteractableEntity): string {
    switch (entity.type) {
      case 'door':
        const door = entity as Door;
        return door.getIsOpen() ? 'Close Door' : 'Open Door';

      case 'vent':
        const vent = entity as Vent;
        if (vent.getIsPlayerInVent()) {
          return 'Exit Vent';
        }
        return vent.getRequiresCrouch() ? 'Enter Vent (Crouch)' : 'Enter Vent';

      case 'hiding_spot':
        const hidingSpot = entity as HidingSpot;
        if (hidingSpot.getIsPlayerHiding()) {
          return 'Exit Hiding Spot';
        }
        if (hidingSpot.isFull()) {
          return 'Hiding Spot Full';
        }
        return `Hide in ${hidingSpot.getSpotType().replace('_', ' ')}`;

      case 'interactive_object':
        const interactiveObj = entity as InteractiveObject;
        const objType = interactiveObj.getObjectType();
        if (interactiveObj.getIsActivated()) {
          return `Deactivate ${objType}`;
        }
        return `Use ${objType}`;

      case 'spawn_point':
        const spawnPoint = entity as SpawnPoint;
        if (spawnPoint.getSpawnType() === 'checkpoint') {
          if (spawnPoint.getIsActivated()) {
            return 'Checkpoint Activated';
          }
          return 'Activate Checkpoint';
        }
        return 'Spawn Point';

      default:
        return 'Interact';
    }
  }

  /**
   * Handle interaction input (E key)
   */
  private handleInteractionInput(): void {
    const inputManager = getInputManager();

    // Check for interaction key press (E key)
    if (inputManager.isKeyPressed('KeyE')) {
      console.log('🔑 E key pressed! Nearby interactables:', this.nearbyInteractables.length);
      if (this.currentPrompt) {
        console.log('📋 Current prompt:', this.currentPrompt.message, 'for', this.currentPrompt.target.type);
      }
      this.tryInteract();
    }
  }

  /**
   * Attempt to interact with the closest interactable
   */
  private tryInteract(): boolean {
    if (!this.player || this.nearbyInteractables.length === 0) {
      return false;
    }

    const target = this.nearbyInteractables[0];
    if (!target) {
      return false;
    }

    const success = this.interactWith(target);

    if (success && this.onInteractionComplete) {
      this.onInteractionComplete(target);
    }

    return success;
  }

  /**
   * Interact with a specific entity
   */
  private interactWith(entity: InteractableEntity): boolean {
    if (!this.player) {
      return false;
    }

    console.log(`InteractionManager: Attempting to interact with ${entity.type} - ${entity.id}`);

    switch (entity.type) {
      case 'door':
        return this.interactWithDoor(entity as Door);

      case 'vent':
        return this.interactWithVent(entity as Vent);

      case 'hiding_spot':
        return this.interactWithHidingSpot(entity as HidingSpot);

      case 'interactive_object':
        return this.interactWithObject(entity as InteractiveObject);

      case 'spawn_point':
        return this.interactWithSpawnPoint(entity as SpawnPoint);

      default:
        console.warn(`InteractionManager: Unknown entity type: ${entity.type}`);
        return false;
    }
  }

  /**
   * Interact with a door - handles teleportation if door has a destination
   */
  private interactWithDoor(door: Door): boolean {
    if (!this.player) return false;

    // If door is already open, close it
    if (door.getIsOpen()) {
      door.close();
      return true;
    }

    // Try to open the door
    const opened = door.tryOpen(this.player);
    if (!opened) return false;

    // Check if this door has a teleport destination
    const transitionInfo = door.getTransitionInfo();
    if (transitionInfo && transitionInfo.spawn) {
      // Store current position before teleporting (for return trips)
      this.lastDoorEntryPosition = { x: this.player.position.x, y: this.player.position.y };
      this.lastDoorId = door.doorId;

      // Find the spawn point to teleport to
      const spawnPoint = this.findSpawnPoint(transitionInfo.spawn);
      if (spawnPoint) {
        // Teleport player to the spawn point
        this.player.position.x = spawnPoint.position.x;
        this.player.position.y = spawnPoint.position.y;
        this.player.velocity.x = 0;
        this.player.velocity.y = 0;
        console.log(`🚪 Player teleported through door '${door.doorId}' to spawn '${transitionInfo.spawn}' at (${spawnPoint.position.x}, ${spawnPoint.position.y})`);
      } else if (this.lastDoorEntryPosition && door.doorId !== this.lastDoorId) {
        // No spawn point found, but we have a previous entry position - teleport back
        this.player.position.x = this.lastDoorEntryPosition.x;
        this.player.position.y = this.lastDoorEntryPosition.y;
        this.player.velocity.x = 0;
        this.player.velocity.y = 0;
        console.log(`🚪 Player returned through door '${door.doorId}' to (${this.lastDoorEntryPosition.x}, ${this.lastDoorEntryPosition.y})`);
        // Clear the entry position after returning
        this.lastDoorEntryPosition = null;
        this.lastDoorId = null;
      }
    }

    return true;
  }

  /**
   * Find a spawn point by ID - uses callback if registered, or searches interactables
   */
  private findSpawnPoint(spawnId: string): { position: { x: number; y: number } } | null {
    // First try the callback if registered
    if (this.onFindSpawnPoint) {
      const position = this.onFindSpawnPoint(spawnId);
      if (position) {
        return { position };
      }
    }

    // Fallback: search registered interactables
    for (const entity of this.interactables.values()) {
      if (entity.type === 'spawn_point' && entity.id === spawnId) {
        return entity as SpawnPoint;
      }
    }

    return null;
  }

  /**
   * Set callback to find spawn points from the level
   */
  public setOnFindSpawnPoint(callback: (spawnId: string) => { x: number; y: number } | null): void {
    this.onFindSpawnPoint = callback;
  }

  /**
   * Get the last door entry position (for return teleportation)
   */
  public getLastDoorEntryPosition(): { x: number; y: number } | null {
    return this.lastDoorEntryPosition;
  }

  /**
   * Clear door entry tracking (e.g., when level changes)
   */
  public clearDoorEntryTracking(): void {
    this.lastDoorEntryPosition = null;
    this.lastDoorId = null;
  }

  /**
   * Interact with a vent - INSTANT teleportation to exit
   */
  private interactWithVent(vent: Vent): boolean {
    if (!this.player) return false;

    if (vent.getIsPlayerInVent()) {
      vent.forceExit();
      return true;
    }

    const entrancePos = vent.getEntrancePosition();
    const exitPos = vent.getExitPosition();
    const distEntrance = this.calculateDistance(this.player.position, entrancePos);
    const distExit = this.calculateDistance(this.player.position, exitPos);

    if (distExit < distEntrance) {
      // Player is at the exit end — teleport them back to the entrance
      this.player.position.x = entrancePos.x;
      this.player.position.y = entrancePos.y;
      this.player.velocity.x = 0;
      this.player.velocity.y = 0;
      console.log(`✨ Player teleported BACK through vent to entrance (${entrancePos.x}, ${entrancePos.y})`);
      return true;
    } else {
      // Player is at the entrance end — teleport to exit
      const success = vent.enter(this.player);
      if (success) {
        this.player.position.x = exitPos.x;
        this.player.position.y = exitPos.y;
        this.player.velocity.x = 0;
        this.player.velocity.y = 0;
        console.log(`✨ Player teleported through vent to (${exitPos.x}, ${exitPos.y})`);
      }
      return success;
    }
  }

  /**
   * Interact with a hiding spot
   */
  private interactWithHidingSpot(hidingSpot: HidingSpot): boolean {
    if (!this.player) return false;

    if (hidingSpot.getIsPlayerHiding()) {
      hidingSpot.exit();
      this.player.unhide();
      return true;
    } else {
      const success = hidingSpot.hide(this.player);
      if (success) {
        this.player.hide();
      }
      return success;
    }
  }

  /**
   * Interact with an interactive object
   */
  private interactWithObject(obj: InteractiveObject): boolean {
    if (!this.player) return false;

    return obj.interact(this.player);
  }

  /**
   * Interact with a spawn point (checkpoint)
   */
  private interactWithSpawnPoint(spawnPoint: SpawnPoint): boolean {
    if (!this.player) return false;

    // Only checkpoints can be activated by interaction
    if (spawnPoint.getSpawnType() !== 'checkpoint') {
      return false;
    }

    // Don't activate if already activated
    if (spawnPoint.getIsActivated()) {
      console.log('✅ Checkpoint already activated');
      return false;
    }

    // Activate the checkpoint via CheckpointSystem (triggers save callback)
    const checkpointSystem = CheckpointSystem.getInstance();
    checkpointSystem.activateCheckpoint(spawnPoint);
    console.log(`✅ Manually activated checkpoint: ${spawnPoint.getCheckpointName()}`);
    return true;
  }

  /**
   * Calculate distance between two positions
   */
  private calculateDistance(
    pos1: { x: number; y: number },
    pos2: { x: number; y: number }
  ): number {
    const dx = pos2.x - pos1.x;
    const dy = pos2.y - pos1.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Set callback for when interaction prompt changes
   */
  public setOnPromptChange(callback: (prompt: InteractionPrompt | null) => void): void {
    this.onPromptChange = callback;
  }

  /**
   * Set callback for when interaction is completed
   */
  public setOnInteractionComplete(callback: (target: InteractableEntity) => void): void {
    this.onInteractionComplete = callback;
  }

  /**
   * Get current interaction prompt
   */
  public getCurrentPrompt(): InteractionPrompt | null {
    return this.currentPrompt;
  }

  /**
   * Get all nearby interactables
   */
  public getNearbyInteractables(): InteractableEntity[] {
    return [...this.nearbyInteractables];
  }

  /**
   * Clear all interactables
   */
  public clear(): void {
    this.interactables.clear();
    this.nearbyInteractables = [];
    this.currentPrompt = null;
    console.log('InteractionManager: Cleared all interactables');
  }

  /**
   * Reset interaction manager
   */
  public reset(): void {
    this.clear();
    this.player = null;
  }
}

// Singleton instance
let interactionManagerInstance: InteractionManager | null = null;

/**
 * Get the singleton InteractionManager instance
 */
export function getInteractionManager(): InteractionManager {
  if (!interactionManagerInstance) {
    interactionManagerInstance = new InteractionManager();
  }
  return interactionManagerInstance;
}
